import { ActionKind, ActionTypes } from '@dynamic/ui';
import { FC } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';

import { CommonAction } from '#/service/config';
import { NavigationType } from '#/service/constants';
import { InternalPageId } from '#/service/constants/menu';
import { destinationRoutes } from '#/service/routes';
import { remify, techSearchParams } from '#/service/utils';

export interface FooterLinkProps {
  title: string;
  action: CommonAction;
}

const getNavigationComponent = ({
  action,
  search,
  title,
}: FooterLinkProps & { search: string }) => {
  switch (action.kind) {
    case ActionKind.NavigateExternal:
      return (
        <LinkContainer>
          <a href={action?.parameters?.link} rel="noreferrer" target="_blank">
            {title}
          </a>
        </LinkContainer>
      );
    case ActionKind.NavigateInternal:
      return (
        <LinkContainer>
          <Link
            to={{
              pathname:
                destinationRoutes[action.parameters?.view as InternalPageId],
              search: techSearchParams(search),
            }}
          >
            {title}
          </Link>
        </LinkContainer>
      );
    case ActionKind.NavigatePage:
      return (
        <LinkContainer>
          <Link
            to={{
              pathname: action.parameters?.pageId,
              search: techSearchParams(search),
            }}
          >
            {title}
          </Link>
        </LinkContainer>
      );
    default:
      return null;
  }
};

export const FooterLink: FC<FooterLinkProps> = ({ title, action }) => {
  const { search } = useLocation();

  const isKindUsed = !!action.kind;

  if (isKindUsed) {
    return getNavigationComponent({ title, action, search });
  }

  // TODO#actions: once kind and parameters are supported by each templates we can remove the following
  // legacy logic to handle legacy actions
  if (action.type === ActionTypes.Navigate) {
    if (action.params?.destination?.type === NavigationType.External) {
      return (
        <LinkContainer>
          <a
            href={action?.params?.destination?.id}
            rel="noreferrer"
            target="_blank"
          >
            {title}
          </a>
        </LinkContainer>
      );
    } else if (action?.params?.destination?.id) {
      return (
        <LinkContainer>
          <Link
            to={{
              pathname: action?.params?.destination?.id,
              search: techSearchParams(search),
            }}
          >
            {title}
          </Link>
        </LinkContainer>
      );
    }
  }

  console.warn('FooterLink: not supported link action', action);

  return null;
};

FooterLink.displayName = 'FooterLink';

const LinkContainer = styled.div`
  margin: ${remify(6)} 0;

  > a {
    font: var(--fontStyleSubtitle);
  }

  > span {
    cursor: pointer;
    opacity: 0.7;

    &:hover {
      opacity: 1;
    }
  }
`;
